import Tabs from "components/Tabs";
import Pagination from "./Pagination";
import Count from "./Count";
import Export from "./Export";
import { createContext, useState } from "react";
import useResponsive from "helpers/useResponsive";
import Desktop from "./layouts/Desktop";
import Mobile from "./layouts/Mobile";
import BasicButton from "components/BasicInputs/BasicButton";
import PlusIcon from "assets/icons/PlusIcon";
import { Tooltip } from "@mui/material";
const xlsx = require("xlsx");
export interface TableContextType extends Partial<TableProps> {
  handleSort?: (i: number, dir: "asc" | "desc") => void;
  handleSearch?: (i: number, text: string) => void;
  state?: TableState;
  setState?: React.Dispatch<React.SetStateAction<TableState>>;
  checkedAll?: number;
  onCheckAll?: (active: boolean) => void;
  bodyChangeKey?: string;
  withSkeleton?: boolean;
}

export const TableContext = createContext<TableContextType>({});

export default function Table(props: TableProps) {
  /* -------------------------------------------------------------------------- */
  /*                                  Variables                                 */
  /* -------------------------------------------------------------------------- */
  const { pagination, tabs, checkbox, count, withExport, cols, tableBorder } = props;
  const [state, setState] = useState<TableState>({});
  const { isMobile } = useResponsive();
  let rows = props.rows || [];
  if (state.sortKey !== undefined && state.sortDir) rows = sort(rows, state.sortKey, state.sortDir);
  if (state.searchKey !== undefined && state.searchText) {
    rows = search(rows, state.searchKey || 0, state.searchText || "");
  }
  rows = paginate(rows, pagination);
  const pages = props.rows?.length;
  let checkedAll = 2;
  if (props.rows && checkbox?.items && checkbox.items.length > props.rows.length) checkedAll = 1;
  else if (checkbox?.items && checkbox.items.length > 0) checkedAll = 3;
  const bodyChangeKey = `${pagination?.page}${checkedAll}${tabs?.activeTab}${count}`;

  /* -------------------------------------------------------------------------- */
  /*                                  Function                                  */
  /* -------------------------------------------------------------------------- */
  function paginate(data: any[], pagination?: TableProps["pagination"]) {
    if (pagination) {
      const { page, perPage } = pagination;
      const newRows = data && data.slice((page - 1) * perPage, page * perPage);
      return newRows;
    } else {
      return data;
    }
  }
  const downloadTable = (type: 'excel' | 'pdf') => {
    if (type === "excel") {
      const data: any = [];
      rows.map((item: any) => {
        const row = {};
        cols.map((title: any) => {
          (row as any)[title["name"]] = title.getValue ? title.getValue(item) : item[title["key"]];
        });
        data.push(row);


      });

      const worksheet = xlsx.utils.json_to_sheet(data);
      const workbook = xlsx.utils.book_new();
      xlsx.utils.book_append_sheet(workbook, worksheet, tabs?.activeTab ?? tabs?.activeTab);
      //let buffer = XLSX.write(workbook, { bookType: "xlsx", type: "buffer" });
      //XLSX.write(workbook, { bookType: "xlsx", type: "binary" });
      xlsx.writeFile(workbook, `${tabs?.activeTab}.xlsx`);
    }
  };
  function onCheckAll(active: boolean) {
    if (!checkbox) return;
    const { key, onChange } = checkbox;
    let arr: any[] = [];
    if (active) {
      props.rows?.map((x) => arr.push(x[key]));
      onChange && onChange(arr);
    } else {
      onChange && onChange(arr);
    }
  }

  function handlePagination(page: number) {
    if (!pagination) return;
    const { perPage, onChange } = pagination;
    onChange && onChange({ page, perPage });
  }

  function sort(rows: any[], i: number, dir: "asc" | "desc") {
    return rows?.sort((a, b) => {
      let val1 = a[cols[i].key];
      let val2 = b[cols[i].key];
      if (dir === "asc") {
        return val1 > val2 ? 1 : val1 < val2 ? -1 : 0;
      } else return val1 < val2 ? 1 : val1 > val2 ? -1 : 0;
    });
  }

  function search(rows: any[], i: number, text: string) {
    return (
      rows?.filter((x) => {
        let val = x[cols[i].key];
        const getValue = cols[i].getValue;
        if (getValue) val = getValue(x);
        if (["string", "number"].includes(typeof val)) {
          return `${val}`.toLowerCase().includes(`${text}`.toLowerCase());
        }
        return false;
      }) || []
    );
  }

  /* -------------------------------------------------------------------------- */
  /*                                     JSX                                    */
  /* -------------------------------------------------------------------------- */
  return (
    <TableContext.Provider value={{ ...props, state, setState, checkedAll, onCheckAll, bodyChangeKey, rows }}>
      {(pagination || props.paginationAction) && (
        <div className="flex justify-between mb-3">
          <div>{props.paginationAction}</div>
          {pagination && (
            <Pagination
              key={pages}
              page={pagination.page}
              pages={Math.ceil(pages ? pages / pagination.perPage : 1)}
              onChange={handlePagination}
            />
          )}
        </div>
      )}
      <div
        className="flex flex-col overflow-hidden rounded-md whitespace-nowrap"
        style={{
          ...(tableBorder !== undefined && !tableBorder
            ? {}
            : { border: "1px solid rgba(100, 100, 100, 0.75)" }),
        }}
      >
        {(pagination || tabs) && (
          <div
            className="flex items-center justify-between py-2 overflow-x-auto md:overflow-hidden"
            style={{ backgroundColor: "#FCFCFC" }}
          >
            <div>{tabs && <Tabs {...tabs} style={{ border: 0 }} />}</div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
              <div />
              {pagination && <Count length={props.rows?.length} {...pagination} />}
              {withExport && <Export downloadTable={downloadTable} />}
              <div />
            </div>
          </div>
        )}
        <div className={props.noScroll ? " z-[2]" : "overflow-auto z-[2]"}>
          {!isMobile || !props.responsive ? <Desktop /> : <Mobile />}
        </div>
        {props.onAddRow && (

          <div className="pt-4">
            <Tooltip title={"کلید میانبر (insert)"}>
              <BasicButton
                onClick={() => {
                  if (!props.onAddRow) return;
                  if (!isMobile) props.onAddRow();
                  else {
                    props.onAddRow().then((res) => {
                      setState((x) => ({ ...x, open: true, openRowId: res.id }));
                    });
                  }
                }}
              >
                <PlusIcon scale={0.7} />
                <span>افزودن ردیف</span>
              </BasicButton>
            </Tooltip>
          </div>

        )}
      </div>
    </TableContext.Provider>
  );
}
